import { useState, useEffect } from 'react'
import { useNotification } from '../../contexts/NotificationContext'
import api from '../../services/api'
import {
  Code,
  Save,
  Loader2,
  RefreshCw,
  Copy,
  Check,
  ExternalLink,
  Eye,
  Info,
  FileCode,
} from 'lucide-react'
import { Panel, Button, buttonClass } from '../../design-system'

interface XmlFeedConfigData {
  id: number
  token: string
  ativo: boolean
  incluirVendidos: boolean
  incluirReservados: boolean
  incluirSemFoto: boolean
  limiteVeiculos: number | null
  statusIncluir: string | null
  marcasExcluir: string | null
  usarPrecoPromocional: boolean
  tipoDestaquePadrao: string | null
  ultimoAcesso: string | null
  totalAcessos: number
  urlFeed: string
}

export default function XmlFeedConfig() {
  const { showApiError, showSuccess } = useNotification()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [regenerando, setRegenerando] = useState(false)
  const [copiado, setCopiado] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [previewXml, setPreviewXml] = useState('')

  const [config, setConfig] = useState<XmlFeedConfigData | null>(null)
  const [form, setForm] = useState({
    ativo: true,
    incluirVendidos: false,
    incluirReservados: true,
    incluirSemFoto: false,
    limiteVeiculos: '',
    statusIncluir: '',
    marcasExcluir: '',
    usarPrecoPromocional: false,
    tipoDestaquePadrao: '',
  })

  useEffect(() => {
    const controller = new AbortController()
    fetchConfig(controller.signal)
    return () => controller.abort()
  }, [])

  const fetchConfig = async (signal?: AbortSignal) => {
    try {
      setLoading(true)
      const response = await api.get('/integracoes/xml/config', { signal })
      if (response.data) {
        setConfig(response.data)
        setForm({
          ativo: response.data.ativo ?? true,
          incluirVendidos: response.data.incluirVendidos ?? false,
          incluirReservados: response.data.incluirReservados ?? true,
          incluirSemFoto: response.data.incluirSemFoto ?? false,
          limiteVeiculos: response.data.limiteVeiculos?.toString() || '',
          statusIncluir: response.data.statusIncluir || '',
          marcasExcluir: response.data.marcasExcluir || '',
          usarPrecoPromocional: response.data.usarPrecoPromocional ?? false,
          tipoDestaquePadrao: response.data.tipoDestaquePadrao || '',
        })
      }
    } catch (error: unknown) {
      if ((error as { name?: string })?.name !== 'CanceledError') {
        // Config pode não existir ainda
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setSaving(true)
      const response = await api.put('/integracoes/xml/config', {
        ativo: form.ativo,
        incluirVendidos: form.incluirVendidos,
        incluirReservados: form.incluirReservados,
        incluirSemFoto: form.incluirSemFoto,
        limiteVeiculos: form.limiteVeiculos ? parseInt(form.limiteVeiculos) : null,
        statusIncluir: form.statusIncluir || null,
        marcasExcluir: form.marcasExcluir || null,
        usarPrecoPromocional: form.usarPrecoPromocional,
        tipoDestaquePadrao: form.tipoDestaquePadrao || null,
      })
      setConfig(response.data)
      showSuccess('Configurações salvas com sucesso!')
    } catch (error) {
      showApiError(error)
    } finally {
      setSaving(false)
    }
  }

  const regenerarToken = async () => {
    if (!window.confirm('Regenerar o token? O token anterior deixará de funcionar.')) return

    try {
      setRegenerando(true)
      const response = await api.post('/integracoes/xml/regenerar-token')
      showSuccess(response.data.message)
      fetchConfig()
    } catch (error) {
      showApiError(error)
    } finally {
      setRegenerando(false)
    }
  }

  const copiarUrl = async () => {
    if (!config) return

    const baseUrl = window.location.origin
    const fullUrl = `${baseUrl}${config.urlFeed}`

    try {
      await navigator.clipboard.writeText(fullUrl)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2000)
    } catch {
      // Fallback para browsers mais antigos
      const textarea = document.createElement('textarea')
      textarea.value = fullUrl
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2000)
    }
  }

  const carregarPreview = async () => {
    try {
      setShowPreview(true)
      const response = await api.get('/integracoes/xml/preview')
      setPreviewXml(response.data)
    } catch (error) {
      showApiError(error)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : ''
  const fullFeedUrl = config ? `${baseUrl}${config.urlFeed}` : ''

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900">
            <Code className="w-7 h-7 text-primary-600" />
            Configuração do XML Feed
          </h1>
          <p className="mt-1 text-slate-500">
            Configure o feed XML para integração automática com plataformas
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulário */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="rounded-2xl bg-white border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.04)] p-6 space-y-6">
            {/* URL do Feed */}
            {config && (
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  URL do Feed
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={fullFeedUrl}
                    readOnly
                    className="input-field flex-1 bg-slate-50"
                  />
                  <Button type="button" variant="secondary" onClick={copiarUrl}>
                    {copiado ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copiado ? 'Copiado!' : 'Copiar'}
                  </Button>
                  <a
                    href={fullFeedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={buttonClass('secondary', 'icon')}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Use esta URL para configurar a integração nos portais que suportam XML
                </p>
              </div>
            )}

            {/* Ativo */}
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={form.ativo}
                onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <span className="text-sm text-slate-700">Feed ativo</span>
            </label>

            <div className="border-t border-slate-100 pt-6">
              <h3 className="text-sm font-bold text-slate-900 mb-4">Filtros de Veículos</h3>

              <div className="space-y-4">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={form.incluirReservados}
                    onChange={(e) => setForm({ ...form, incluirReservados: e.target.checked })}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <span className="text-sm text-slate-700">Incluir veículos reservados</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={form.incluirVendidos}
                    onChange={(e) => setForm({ ...form, incluirVendidos: e.target.checked })}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <span className="text-sm text-slate-700">Incluir veículos vendidos</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={form.incluirSemFoto}
                    onChange={(e) => setForm({ ...form, incluirSemFoto: e.target.checked })}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <span className="text-sm text-slate-700">Incluir veículos sem foto</span>
                </label>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">
                    Limite de veículos
                  </label>
                  <input
                    type="number"
                    value={form.limiteVeiculos}
                    onChange={(e) => setForm({ ...form, limiteVeiculos: e.target.value })}
                    className="input-field w-32"
                    placeholder="Sem limite"
                    min="0"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Deixe em branco para incluir todos os veículos
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-6">
              <h3 className="text-sm font-bold text-slate-900 mb-4">Configurações de Preço</h3>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={form.usarPrecoPromocional}
                  onChange={(e) => setForm({ ...form, usarPrecoPromocional: e.target.checked })}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <span className="text-sm text-slate-700">
                  Usar preço promocional quando disponível
                </span>
              </label>
            </div>

            <div className="border-t border-slate-100 pt-6">
              <h3 className="text-sm font-bold text-slate-900 mb-4">Destaque Padrão</h3>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Tipo de destaque padrão
                </label>
                <select
                  value={form.tipoDestaquePadrao}
                  onChange={(e) => setForm({ ...form, tipoDestaquePadrao: e.target.value })}
                  className="input-field w-full"
                >
                  <option value="">Nenhum (usar destaque do veículo)</option>
                  <option value="NORMAL">Normal</option>
                  <option value="DESTAQUE">Destaque</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Será usado quando o veículo não tiver um tipo de destaque definido
                </p>
              </div>
            </div>

            <div className="flex justify-between pt-4 border-t border-slate-100">
              <Button type="button" variant="secondary" onClick={carregarPreview}>
                <Eye className="w-4 h-4" />
                Preview XML
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Salvar Configurações
              </Button>
            </div>
          </form>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Estatísticas */}
          {config && (
            <Panel title="Estatísticas">
              <div className="space-y-4">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Total de Acessos</p>
                  <p className="text-2xl font-extrabold tracking-tight tabular-nums text-slate-900">{config.totalAcessos}</p>
                </div>

                {config.ultimoAcesso && (
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Último Acesso</p>
                    <p className="text-sm text-slate-600">
                      {new Date(config.ultimoAcesso).toLocaleString('pt-BR')}
                    </p>
                  </div>
                )}
              </div>
            </Panel>
          )}

          {/* Regenerar Token */}
          {config && (
            <Panel title="Segurança">
              <p className="text-sm text-slate-600 mb-4">
                Se o token do feed foi comprometido, você pode gerar um novo. O token anterior deixará de funcionar.
              </p>

              <Button
                variant="secondary"
                onClick={regenerarToken}
                disabled={regenerando}
                className="w-full"
              >
                {regenerando ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Regenerar Token
              </Button>
            </Panel>
          )}

          {/* Info */}
          <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-sky-800">Como usar</p>
                <p className="text-sm text-sky-700 mt-1">
                  Copie a URL do feed e configure no portal desejado. O feed é atualizado automaticamente quando você faz alterações nos veículos.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Preview */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <FileCode className="w-5 h-5 text-slate-400" />
                Preview do XML Feed
              </h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <pre className="text-xs bg-slate-900 text-emerald-400 p-4 rounded-xl overflow-x-auto">
                {previewXml || 'Carregando...'}
              </pre>
            </div>
            <div className="flex justify-end p-4 border-t border-slate-100">
              <Button variant="secondary" onClick={() => setShowPreview(false)}>
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
